import { FC, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Input, message } from 'antd'
import { IconButton, Alert, Box } from '../../../../../ui'
import useRequest from '../../../../../hooks/useRequest'
import { getConsuming, getOwnershipDetail } from '../../../../config'
import style from './style.m.less'
import { Card, OwnershipCard } from '../../..'
import { useTranslation } from 'react-i18next'

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

const Check: FC = () => {
    const { t } = useTranslation()
    // const [certificateNumber, setCertificateNumber] = useState('')
    const [certificateNumber, setCertificateNumber] = useState(() => {
        if (localStorage.getItem('green_tools_certificate_number')) {
            return localStorage.getItem('green_tools_certificate_number')
        } else {
            localStorage.setItem('green_tools_certificate_number', '')
            return ''
        }
    })
    // const [documentNumber, setDocumentNumber] = useState('')
    const [documentNumber, setDocumentNumber] = useState(() => {
        if (localStorage.getItem('green_tools_document_number')) {
            return localStorage.getItem('green_tools_document_number')
        } else {
            localStorage.setItem('green_tools_document_number', '')
            return ''
        }
    })
    const [isInitializedFromQuery, setIsInitializedFromQuery] = useState(false)
    const [loadingSearch, setLoadingSearch] = useState<boolean>(false)
    const { fetch, result, dropState } = useRequest(getConsuming)
    const {
        fetch: fetchOwnershipDetail,
        result: ownershipDetailResult,
        dropState: dropOwnershipState,
    } = useRequest(getOwnershipDetail)
    const location = useLocation()

    const onChangeCertificateNumber = (e: any) => {
        setCertificateNumber(e.target.value)
        localStorage.setItem('green_tools_certificate_number', e.target.value)
    }

    const onChangeDocumentNumber = (e: any) => {
        setDocumentNumber(e.target.value)
        localStorage.setItem('green_tools_document_number', e.target.value)
    }

    const dropStates = () => {
        dropState()
        dropOwnershipState()
    }

    const onFinish = () => {
        setLoadingSearch(true)
        if (!certificateNumber) {
            message.error(t('greenTools.errorEmptyGTNumber'))
        } else if (!documentNumber) {
            message.error(t('greenTools.errorEmptyDetail'))
        } else {
            const trimmedCertNumber = certificateNumber.trim()
            const trimmedDocNumber = documentNumber.trim()

            if (UUID_REGEX.test(trimmedCertNumber)) {
                dropStates()
                fetchOwnershipDetail({
                    getParams: {
                        ownership_certificate_id: trimmedCertNumber,
                        ...(trimmedDocNumber ? { additional_info: trimmedDocNumber } : {}),
                    },
                    onSuccess: (_) => {
                        setLoadingSearch(false)
                    },
                    onError: (err) => {
                        setLoadingSearch(false)
                        message.error(err.detail)
                    },
                    showError: false,
                })
            } else {
                dropStates()
                fetch({
                    body: {
                        limit: 100,
                        offset: 0,
                        filters: {
                            green_tool_number: trimmedCertNumber,
                            inn_snils_ogrn_personal_account_number: trimmedDocNumber,
                        },
                    },
                    onError: () => {
                        setLoadingSearch(false)
                        dropState()
                    },
                    errorCodeMessage: true,
                    onSuccess: (data) => {
                        setLoadingSearch(false)
                        if (!data.consumers.length) {
                            message.error(t('greenTools.errorNotFound'))
                        }
                    },
                })
            }
        }
    }

    useEffect(() => {
        const params = new URLSearchParams(location.search)
        const certificate = params.get('certificate_number')
        const document = params.get('document_number')

        if (certificate && document) {
            setCertificateNumber(certificate)
            localStorage.setItem('green_tools_certificate_number', certificate)
            setDocumentNumber(document)
            localStorage.setItem('green_tools_document_number', document)
            setIsInitializedFromQuery(true)
        }
    }, [location.search])

    useEffect(() => {
        if (location.state && location.state.fromMenu) {
            if (isInitializedFromQuery && certificateNumber && documentNumber) {
                onFinish()
                setIsInitializedFromQuery(false)
            }
        } else {
            if (certificateNumber && documentNumber) {
                onFinish()
            }
        }
    }, [isInitializedFromQuery])

    const renderCards = () => {
        if (ownershipDetailResult) {
            return <OwnershipCard data={ownershipDetailResult} />
        }
        if (result?.consumers?.length) {
            return (
                <Box gap={'16px'}>
                    {result.consumers.map((item: any, index: number) => (
                        <Card key={index} item={item} />
                    ))}
                </Box>
            )
        }
        return null
    }

    return (
        <>
            <div className={style.selectorWrapper}>
                <Alert
                    message={
                        <div>
                            <div>{t('greenTools.checkAlertStart')}</div>
                            <div>{t('greenTools.checkAlertEnd')}</div>
                        </div>
                    }
                    type="info"
                    className={style.alert}
                />
                <div className={style.numberInputWrapper}>
                    <div>
                        <div>{t('greenTools.toolNumberLabel')}</div>
                        <div className={style.inputBlock}>
                            <Input
                                value={certificateNumber}
                                onChange={onChangeCertificateNumber}
                                className={style.certificateNumberInput}
                                placeholder={t('greenTools.toolNumberPlaceholder')}
                            />
                        </div>
                    </div>
                    <div className={style.leftGapBlock}>
                        <div>{t('greenTools.detailLabel')}</div>
                        <div className={style.inputBlock}>
                            <Input
                                value={documentNumber}
                                onChange={onChangeDocumentNumber}
                                className={style.documentNumberInput}
                                placeholder={t('greenTools.detailPlaceholder')}
                            />
                        </div>
                    </div>
                    <div className={style.leftGapBlock}>
                        <IconButton
                            onClick={onFinish}
                            className={style.searchButton}
                            customStyle={{ height: '32px' }}
                            loading={loadingSearch}
                        >
                            {t('greenTools.search')}
                        </IconButton>
                    </div>
                </div>
            </div>
            <div className={style.resultsWrapper}>{renderCards()}</div>
        </>
    )
}

export default Check
